import type { Activity, MessageActivity, MessageActivityDetails, NoteActivityDetails } from '@paintscout/api'
// import { getEmailTemplates } from '@paintscout/util/builder'
import type { OptionsDocument } from 'paintscout'
import { getCrews } from '../../builder/options/get-crews'
import { getEmailTemplates } from '../../builder/options/get-email-templates'
import { removeAuth0Prefix } from '../../users/removeAuth0Prefix'

export type ActivityVariant = 'user' | 'customer' | 'automation' | 'other-user' | 'automated-customer'

export function getActivityVariant(activity: Activity, options: OptionsDocument): ActivityVariant {
  if ((activity.details as any).crew) {
    const crewOptions = getCrews({ options, searchCrewValue: '' })
    const crew = crewOptions.find((crew) => crew._id === (activity.details as any).crew)
    if (removeAuth0Prefix(crew?.projectManager?.id) !== removeAuth0Prefix(activity.details?.owner?.id)) {
      return 'other-user'
    }
  }

  const ownerMismatch =
    !!activity?.details?.owner?.id &&
    !!activity?.details?.quoteOwner?.id &&
    removeAuth0Prefix(activity.details.owner.id) !== removeAuth0Prefix(activity.details.quoteOwner.id)

  switch (activity.details?.activityType) {
    case 'message': {
      const isCustomer = (activity?.details as MessageActivityDetails)?.message?.from?.isCustomer
      const isAutomated = (activity?.details as MessageActivityDetails)?.message?.from?.isAutomated
      if (!isAutomated && !isCustomer && !ownerMismatch) {
        return getLegacyMessageActivityVariant(activity as MessageActivity, options)
      }
      return isAutomated ? 'automation' : isCustomer ? 'customer' : ownerMismatch ? 'other-user' : 'user'
    }
    case 'thank-you-sent':
    case 'declined-sent':
    case 'payment-received-sent':
    case 'follow-up-sent':
    case 'event-email-sent':
      return 'automation'
    case 'note': {
      const versions = (activity.details as NoteActivityDetails)?.versions ?? []
      if (versions.length > 0) {
        const latestVersion = versions[0]
        if (removeAuth0Prefix(latestVersion?.owner?.id) !== removeAuth0Prefix(activity.details?.owner?.id)) {
          return 'other-user'
        }
      }
      return 'user'
    }
    case 'lead-form-submission':
      return 'automated-customer'
    case 'deal-created': {
      if (activity.details?.isAutomated && !activity.details?.owner && !activity.details?.quoteOwner) {
        return 'automated-customer'
      }
      // No break here, we want it to fall through to default case if it's a regular deal creation
    }
    default: {
      const isAutomated = activity.details?.isAutomated
      return isAutomated ? 'automation' : ownerMismatch ? 'other-user' : 'user'
    }
  }
}

function getLegacyMessageActivityVariant(activity: MessageActivity, options: OptionsDocument): ActivityVariant {
  const emailTemplates = getEmailTemplates({ options })
  const automaticTemplates = [
    emailTemplates['accept-thank-you'],
    emailTemplates['decline-thank-you'],
    emailTemplates['payment-received']
  ]
    .map((template) => template?.body)
    .filter(Boolean)

  const messageContent = activity.details?.message?.text

  // Try to match messageContent to any template
  const isAutomated = automaticTemplates.some((body) => {
    if (!body) {
      return false
    }
    const regex = templateToRegex(body)
    return regex.test(messageContent)
  })

  return isAutomated ? 'automation' : 'user'
}

function templateToRegex(template: string): RegExp {
  try {
    // Escape all regex special characters, including curly braces
    const escaped = template.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
    // Replace escaped {{...}} with a non-greedy wildcard
    const pattern = escaped.replace(/\\\{\\\{.*?\\\}\\\}/g, '.*?')
    // Match the entire string, case-insensitive
    // eslint-disable-next-line security/detect-non-literal-regexp
    return new RegExp(`^${pattern}$`, 'i')
    // eslint-disable-next-line unused-imports/no-unused-vars
  } catch (error) {
    return /$^/ // Always-failing regex
  }
}
